import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Facebook, Instagram, CheckCircle2, XCircle, Loader2, ExternalLink,
  Trash2, ShieldCheck, FileText, Users, RefreshCw, Megaphone,
} from "lucide-react";

/**
 * Facebook Pages + Instagram connect wizard, driven by Facebook Login for
 * Business (FLB). Uses META_PAGES_CONFIGURATION_ID.
 *
 * 3 states: loading → not_connected → connected. When not_connected the
 * salon clicks "Connetti Facebook & Instagram", picks a Page from the list
 * returned by /me/accounts, and we persist it.
 */
export default function MetaPagesSection() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [sdkReady, setSdkReady] = useState(false);
  const [pages, setPages] = useState(null); // page picker list
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);
  const [subscribing, setSubscribing] = useState(false);
  const [adCfg, setAdCfg] = useState(null);
  const [adAccounts, setAdAccounts] = useState(null); // picker list (null = closed)
  const [adBusy, setAdBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/meta/pages/config");
      setCfg(r.data);
    } catch { toast.error("Errore caricamento config Meta"); }
    try {
      const ra = await api.get("/meta/ad-account/config");
      setAdCfg(ra.data);
    } catch { setAdCfg(null); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const openAdPicker = useCallback(async () => {
    setAdBusy(true);
    try {
      const r = await api.get("/meta/ad-accounts");
      if (r.data?.needs_reconnect) {
        toast.error("Ricollega Meta per autorizzare l'accesso agli account pubblicitari");
        setAdCfg((p) => ({ ...(p || {}), needs_reconnect: true }));
      } else {
        setAdAccounts(r.data.accounts || []);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore caricamento account pubblicitari");
    }
    setAdBusy(false);
  }, []);

  const selectAdAccount = useCallback(async (acc) => {
    setAdBusy(true);
    try {
      await api.post("/meta/ad-accounts/select", { ad_account_id: acc.ad_account_id });
      toast.success(`Account pubblicitario collegato: ${acc.name || acc.ad_account_id}`);
      setAdAccounts(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore selezione account");
    }
    setAdBusy(false);
  }, [load]);

  const resubscribe = useCallback(async () => {
    setSubscribing(true);
    try {
      const r = await api.post("/meta/pages/subscribe");
      if (r.data?.subscribed) toast.success("Webhook leadgen attivati");
      else toast.error("Sottoscrizione non riuscita: ricollega la Pagina");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore sottoscrizione webhook");
    } finally {
      setSubscribing(false);
      load();
    }
  }, [load]);

  /* --- FB SDK loader (reuses global window.FB if already loaded by WhatsApp section) --- */
  useEffect(() => {
    if (!cfg?.app_id) return;
    if (window.FB) { setSdkReady(true); return; }
    window.fbAsyncInit = () => {
      window.FB.init({ appId: cfg.app_id, cookie: true, xfbml: false,
                       version: cfg.graph_version || "v22.0" });
      setSdkReady(true);
    };
    const s = document.createElement("script");
    s.async = true; s.defer = true; s.crossOrigin = "anonymous";
    s.src = "https://connect.facebook.net/en_US/sdk.js";
    document.body.appendChild(s);
  }, [cfg?.app_id, cfg?.graph_version]);

  const startConnect = () => {
    if (!sdkReady) return toast.error("SDK Facebook in caricamento…");
    if (!cfg?.configuration_id) return toast.error(
      "Configuration ID mancante: aggiungi META_PAGES_CONFIGURATION_ID al backend"
    );
    setConnecting(true);
    const handleResponse = async (resp) => {
      // FLB user-token config: SDK returns the access_token directly. No
      // server-side code exchange, no redirect_uri needed.
      const accessToken = resp?.authResponse?.accessToken;
      if (!accessToken) { setConnecting(false); return toast.error("Login annullato"); }
      try {
        const r = await api.post("/meta/pages/connect", { access_token: accessToken });
        const list = r.data.pages || [];
        if (list.length === 0) toast.error("Nessuna Pagina disponibile");
        else if (list.length === 1) await selectPage(list[0]);
        else setPages(list);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Errore connect");
      }
      setConnecting(false);
    };
    window.FB.login((resp) => { handleResponse(resp); }, {
      config_id: cfg.configuration_id,
    });
  };

  const selectPage = async (p) => {
    try {
      const r = await api.post("/meta/pages/select", {
        page_id: p.page_id,
        page_access_token: p.page_access_token,
        page_name: p.page_name,
        instagram_business_account_id: p.instagram?.id || null,
      });
      toast.success(
        `Collegato: ${r.data.facebook.page_name}` +
        (r.data.instagram ? ` + IG @${r.data.instagram.username || r.data.instagram.business_account_id}` : "")
      );
      setPages(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore salvataggio Pagina");
    }
  };

  const disconnect = async () => {
    try {
      await api.post("/meta/pages/disconnect");
      toast.success("Facebook & Instagram scollegati");
      setConfirmDisconnect(false);
      load();
    } catch { toast.error("Errore"); }
  };

  if (loading) return (
    <div className="sp-card p-8 flex items-center justify-center text-sm text-[color:var(--sp-text-secondary)]">
      <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Carico impostazioni Meta…
    </div>
  );

  const fbConnected = !!cfg?.facebook?.page_id;
  const igConnected = !!cfg?.instagram?.business_account_id;

  return (
    <div data-testid="settings-meta-pages" className="space-y-6">
      <div className="sp-card p-8 space-y-4">
        <div className="flex items-center gap-3">
          <Facebook className="h-5 w-5 text-[color:var(--sp-brand)]" />
          <h2 className="font-display text-xl font-black">Pagina Facebook &amp; Instagram Business</h2>
        </div>
        <p className="text-sm text-[color:var(--sp-text-secondary)]">
          Collega la Pagina Facebook del salone e l&apos;account Instagram Business associato per
          ricevere Lead Ads e messaggi Instagram Direct direttamente nell&apos;Inbox di Memento.
        </p>

        {(fbConnected || igConnected) ? (
          <div className="space-y-3">
            {fbConnected && (
              <div className="rounded-2xl border border-blue-200 bg-blue-50/50 p-4 flex items-start gap-4">
                {cfg.facebook.page_picture_url ? (
                  <img src={cfg.facebook.page_picture_url} alt=""
                    className="h-14 w-14 rounded-full border-2 border-white shadow-sm flex-shrink-0" />
                ) : (
                  <div className="h-14 w-14 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0">
                    <Facebook className="h-6 w-6 text-white" />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 text-blue-700 font-bold text-sm">
                    <Facebook className="h-3.5 w-3.5" /> Facebook Page collegata
                  </div>
                  <div className="text-base font-bold mt-1 truncate">{cfg.facebook.page_name || "—"}</div>
                  <div className="text-xs text-[color:var(--sp-text-muted)] mt-1">
                    {cfg.facebook.page_category && <span>{cfg.facebook.page_category} · </span>}
                    ID <code>{cfg.facebook.page_id}</code>
                  </div>
                  <div className="text-xs mt-1">
                    {cfg.facebook.subscribed ? (
                      <span className="text-emerald-600 font-medium"><ShieldCheck className="inline h-3 w-3" /> webhook attivi</span>
                    ) : (
                      <span className="inline-flex items-center gap-2">
                        <span className="text-amber-600">webhook non sottoscritti</span>
                        <button onClick={resubscribe} disabled={subscribing}
                          data-testid="meta-resubscribe-btn"
                          className="text-xs text-[color:var(--sp-brand)] underline disabled:opacity-50">
                          {subscribing ? "…" : "Riprova"}
                        </button>
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )}
            {igConnected && (
              <div className="rounded-2xl border border-purple-200 bg-gradient-to-br from-purple-50/50 to-pink-50/50 p-4 flex items-start gap-4">
                {cfg.instagram.profile_picture_url ? (
                  <img src={cfg.instagram.profile_picture_url} alt=""
                    className="h-14 w-14 rounded-full border-2 border-white shadow-sm flex-shrink-0" />
                ) : (
                  <div className="h-14 w-14 rounded-full bg-gradient-to-br from-purple-500 via-pink-500 to-orange-400 flex items-center justify-center flex-shrink-0">
                    <Instagram className="h-6 w-6 text-white" />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 text-purple-700 font-bold text-sm">
                    <Instagram className="h-3.5 w-3.5" /> Instagram Business collegato
                  </div>
                  <div className="text-base font-bold mt-1">@{cfg.instagram.username || cfg.instagram.business_account_id}</div>
                  {cfg.instagram.name && <div className="text-xs">{cfg.instagram.name}</div>}
                  {cfg.instagram.followers_count != null && (
                    <div className="text-xs text-[color:var(--sp-text-muted)] mt-0.5">
                      {new Intl.NumberFormat("it-IT").format(cfg.instagram.followers_count)} follower
                    </div>
                  )}
                </div>
              </div>
            )}
            {/* Account pubblicitario Meta (META FASE 2B) */}
            {fbConnected && (
              <div data-testid="meta-ad-account-card"
                   className="rounded-2xl border border-emerald-200 bg-emerald-50/40 p-4 flex items-start gap-4">
                <div className="h-14 w-14 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0">
                  <Megaphone className="h-6 w-6 text-white" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 text-emerald-700 font-bold text-sm">
                    <Megaphone className="h-3.5 w-3.5" /> Account pubblicitario
                  </div>
                  {adCfg?.selected ? (
                    <>
                      <div className="text-base font-bold mt-1 truncate" data-testid="ad-account-name">
                        {adCfg.selected.name || adCfg.selected.ad_account_id}
                      </div>
                      <div className="text-xs text-[color:var(--sp-text-muted)] mt-1">
                        {adCfg.selected.currency && <span>{adCfg.selected.currency} · </span>}
                        {adCfg.selected.timezone_name || "—"}
                        {adCfg.selected.account_id && <> · ID {adCfg.selected.account_id}</>}
                      </div>
                      <button onClick={openAdPicker} disabled={adBusy}
                        data-testid="ad-account-change-btn"
                        className="mt-2 text-xs text-[color:var(--sp-brand)] underline disabled:opacity-50">
                        {adBusy ? "…" : "Cambia account"}
                      </button>
                    </>
                  ) : adCfg?.needs_reconnect ? (
                    <>
                      <div className="text-sm mt-1 text-amber-700">
                        Ricollega Meta per autorizzare l&apos;accesso all&apos;account pubblicitario.
                      </div>
                      <button onClick={startConnect} disabled={!sdkReady || connecting}
                        data-testid="ad-account-reconnect-btn"
                        className="mt-2 inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-lg bg-[color:var(--sp-brand)] text-white disabled:opacity-50">
                        <RefreshCw className="h-3 w-3" /> Ricollega Meta
                      </button>
                    </>
                  ) : (
                    <>
                      <div className="text-sm mt-1 text-[color:var(--sp-text-muted)]">Nessun account selezionato</div>
                      <button onClick={openAdPicker} disabled={adBusy}
                        data-testid="ad-account-select-btn"
                        className="mt-2 inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-lg bg-emerald-600 text-white disabled:opacity-50">
                        {adBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Megaphone className="h-3 w-3" />}
                        Seleziona account
                      </button>
                    </>
                  )}
                </div>
              </div>
            )}
            <div className="flex justify-end">
              <button
                onClick={() => setConfirmDisconnect(true)}
                data-testid="meta-pages-disconnect-btn"
                className="text-sm text-red-600 hover:text-red-700 font-medium flex items-center gap-1.5"
              >
                <Trash2 className="h-3.5 w-3.5" /> Scollega
              </button>
            </div>
          </div>
        ) : (
          <div className="rounded-2xl border border-dashed border-[color:var(--sp-border)] p-8 text-center">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="h-12 w-12 rounded-full bg-blue-500 flex items-center justify-center">
                <Facebook className="h-6 w-6 text-white" />
              </div>
              <div className="h-12 w-12 rounded-full bg-gradient-to-br from-purple-500 via-pink-500 to-orange-400 flex items-center justify-center">
                <Instagram className="h-6 w-6 text-white" />
              </div>
            </div>
            <div className="text-sm text-[color:var(--sp-text-secondary)] mb-4">
              Nessuna Pagina collegata
            </div>
            <button
              onClick={startConnect}
              disabled={!sdkReady || connecting || !cfg?.configuration_id}
              data-testid="meta-pages-connect-btn"
              className="sp-btn-primary inline-flex items-center gap-2"
            >
              {connecting ? <Loader2 className="h-4 w-4 animate-spin" /> : <ExternalLink className="h-4 w-4" />}
              {connecting ? "Connessione…" : "Connetti Facebook & Instagram"}
            </button>
            {!cfg?.configuration_id && (
              <div className="mt-3 text-xs text-amber-600">
                ⚠️ META_PAGES_CONFIGURATION_ID non configurato sul backend
              </div>
            )}
            <div className="mt-3 text-xs text-[color:var(--sp-text-muted)]">
              Ti sarà chiesto l&apos;accesso alle tue Pagine Facebook e Instagram Business associate.
            </div>
          </div>
        )}
      </div>

      {/* Form Lead Ads attivi — visibile solo quando la Pagina è connessa */}
      {fbConnected && <LeadFormsInline />}

      {pages && (
        <PagePickerDialog pages={pages} onSelect={selectPage} onClose={() => setPages(null)} />
      )}
      {adAccounts && (
        <AdAccountPickerDialog accounts={adAccounts} busy={adBusy}
          onSelect={selectAdAccount} onClose={() => setAdAccounts(null)} />
      )}
      {confirmDisconnect && (
        <ConfirmDisconnectDialog
          fbName={cfg?.facebook?.page_name}
          igName={cfg?.instagram?.username}
          onCancel={() => setConfirmDisconnect(false)}
          onConfirm={disconnect}
        />
      )}
    </div>
  );
}

function ConfirmDisconnectDialog({ fbName, igName, onCancel, onConfirm }) {
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50" onClick={onCancel}>
      <div className="bg-white rounded-2xl w-full max-w-md p-6 space-y-4" onClick={(e) => e.stopPropagation()} data-testid="meta-pages-disconnect-dialog">
        <div className="flex items-center gap-2 text-red-600">
          <Trash2 className="h-5 w-5" />
          <h3 className="font-display text-lg font-black">Scollegare Facebook & Instagram?</h3>
        </div>
        <p className="text-sm text-[color:var(--sp-text-secondary)]">
          Verranno rimossi:
        </p>
        <ul className="text-sm space-y-1 pl-4">
          {fbName && <li>· <b>Facebook Page</b>: {fbName}</li>}
          {igName && <li>· <b>Instagram</b>: @{igName}</li>}
        </ul>
        <div className="text-xs text-[color:var(--sp-text-muted)] bg-amber-50 border border-amber-200 rounded-lg p-3">
          I nuovi Lead Ads e messaggi Instagram <b>non arriveranno più</b> finché non ricolleghi.
          I lead già importati e i messaggi ricevuti restano salvati.
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onCancel} className="px-3 py-1.5 text-sm rounded-lg border border-[color:var(--sp-border)]"
            data-testid="meta-pages-disconnect-cancel">
            Annulla
          </button>
          <button onClick={onConfirm}
            className="px-3 py-1.5 text-sm rounded-lg bg-red-600 hover:bg-red-700 text-white font-bold"
            data-testid="meta-pages-disconnect-confirm">
            Sì, scollega
          </button>
        </div>
      </div>
    </div>
  );
}

function AdAccountPickerDialog({ accounts, busy, onSelect, onClose }) {
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md p-6 space-y-4" onClick={(e) => e.stopPropagation()} data-testid="ad-account-picker">
        <h3 className="font-display text-lg font-black">Seleziona account pubblicitario</h3>
        {accounts.length === 0 ? (
          <p className="text-sm text-[color:var(--sp-text-secondary)]" data-testid="ad-account-empty">
            Non risultano account pubblicitari accessibili con questa connessione Meta.
          </p>
        ) : (
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {accounts.map((a) => (
              <button
                key={a.ad_account_id}
                disabled={busy}
                onClick={() => onSelect(a)}
                className="w-full text-left px-4 py-3 rounded-xl border border-[color:var(--sp-border)] hover:border-emerald-500 hover:bg-emerald-50/50 transition-colors disabled:opacity-50"
                data-testid={`ad-account-pick-${a.ad_account_id}`}
              >
                <div className="flex items-center gap-3">
                  <Megaphone className="h-4 w-4 text-emerald-600" />
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-sm truncate">{a.name || a.ad_account_id}</div>
                    <div className="text-[11px] text-[color:var(--sp-text-muted)]">
                      {a.currency && <>{a.currency} · </>}ID {a.account_id || a.ad_account_id}
                    </div>
                  </div>
                  {a.account_status === 1 && <CheckCircle2 className="h-4 w-4 text-emerald-600" />}
                </div>
              </button>
            ))}
          </div>
        )}
        <div className="flex justify-end">
          <button onClick={onClose} className="text-sm px-3 py-1.5 rounded-lg border border-[color:var(--sp-border)]">Chiudi</button>
        </div>
      </div>
    </div>
  );
}


function PagePickerDialog({ pages, onSelect, onClose }) {
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md p-6 space-y-4" onClick={(e) => e.stopPropagation()} data-testid="meta-pages-picker">
        <h3 className="font-display text-lg font-black">Scegli la Pagina del salone</h3>
        <p className="text-sm text-[color:var(--sp-text-secondary)]">Hai più Pagine Facebook. Seleziona quella del salone:</p>
        <div className="space-y-2 max-h-80 overflow-y-auto">
          {pages.map((p) => (
            <button
              key={p.page_id}
              onClick={() => onSelect(p)}
              className="w-full text-left px-4 py-3 rounded-xl border border-[color:var(--sp-border)] hover:border-[color:var(--sp-brand)] hover:bg-[color:var(--sp-subtle)] transition-colors"
              data-testid={`meta-pages-pick-${p.page_id}`}
            >
              <div className="flex items-center gap-3">
                <Facebook className="h-4 w-4 text-blue-600" />
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-sm truncate">{p.page_name}</div>
                  <div className="text-[11px] text-[color:var(--sp-text-muted)]">
                    ID {p.page_id}
                    {p.instagram && <> · <Instagram className="inline h-3 w-3" /> @{p.instagram.username || p.instagram.id}</>}
                  </div>
                </div>
                <CheckCircle2 className="h-4 w-4 text-[color:var(--sp-brand)] opacity-0" />
              </div>
            </button>
          ))}
        </div>
        <div className="flex justify-end">
          <button onClick={onClose} className="text-sm px-3 py-1.5 rounded-lg border border-[color:var(--sp-border)]">Annulla</button>
        </div>
      </div>
    </div>
  );
}


function LeadFormsInline() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const r = await api.get("/meta/lead-forms");
      setRows(r.data.data || []);
    } catch (e) {
      setError(e?.response?.data?.detail || "Errore caricamento form");
    }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="sp-card p-6 space-y-4" data-testid="meta-lead-forms-section">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <FileText className="h-5 w-5 text-[color:var(--sp-brand)]" />
          <h3 className="font-display text-lg font-black">Form Lead Ads attivi</h3>
        </div>
        <button onClick={load} data-testid="meta-lead-forms-refresh"
          className="text-xs inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border border-[color:var(--sp-border)] hover:bg-[color:var(--sp-subtle)]">
          <RefreshCw className="h-3 w-3" /> Aggiorna
        </button>
      </div>
      <p className="text-xs text-[color:var(--sp-text-secondary)]">
        Form Lead Generation collegati alla Pagina Facebook: quanti lead hai ricevuto e le domande di ciascun form.
      </p>
      {loading ? (
        <div className="py-6 text-sm text-[color:var(--sp-text-muted)] text-center">Carico dai server Meta…</div>
      ) : error ? (
        <div className="py-4 text-xs text-red-600 flex items-center gap-2">
          <XCircle className="h-4 w-4" /> {error}
        </div>
      ) : rows.length === 0 ? (
        <div className="py-6 text-center text-sm text-[color:var(--sp-text-muted)]">
          Nessun form. Crea un form Lead Ads su Meta Ads Manager — apparirà qui.
        </div>
      ) : (
        <ul className="space-y-2" data-testid="meta-lead-forms-list">
          {rows.map((f) => (
            <li key={f.id} className="border border-[color:var(--sp-border)] rounded-xl p-3">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <div className="font-bold text-sm truncate">{f.name || `Form ${f.id}`}</div>
                    <FormStatusPill status={f.status} />
                  </div>
                  <div className="text-[11px] text-[color:var(--sp-text-muted)] mt-1 flex items-center gap-3 flex-wrap">
                    <span className="inline-flex items-center gap-1">
                      <Users className="h-3 w-3" /> {f.leads_count ?? 0} lead
                    </span>
                    <span>ID <code>{f.id}</code></span>
                    <span>Lingua {f.locale || "—"}</span>
                    <span>{new Date(f.created_time).toLocaleDateString("it-IT")}</span>
                  </div>
                </div>
              </div>
              {f.questions && f.questions.length > 0 && (
                <details className="mt-2 text-xs">
                  <summary className="cursor-pointer text-[color:var(--sp-brand)] font-medium">
                    Domande ({f.questions.length})
                  </summary>
                  <ul className="mt-1 pl-4 list-disc space-y-0.5 text-[color:var(--sp-text-secondary)]">
                    {f.questions.map((q, i) => (<li key={i}>{q.label || q.key || q.type}</li>))}
                  </ul>
                </details>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function FormStatusPill({ status }) {
  const s = String(status || "").toUpperCase();
  const cls = s === "ACTIVE" ? "bg-emerald-100 text-emerald-700" :
              s === "ARCHIVED" ? "bg-neutral-100 text-neutral-600" :
              "bg-amber-100 text-amber-700";
  return <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${cls}`}>{s || "—"}</span>;
}
